import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/lib/api/client";
import { courseContentApi, type ContentMode, type CourseContent } from "@/lib/api/courseContent";
import type { Read } from "@/components/ui/box.schema";
export function useCourseContent(course: string, mode: ContentMode, enabled = true) {
  const { me } = useAuth();
  return useQuery({ queryKey: ["course-content", me?.tenant_id, me?.user_id, course, mode],
    queryFn: () => courseContentApi.read(course, mode), enabled: enabled && !!course, retry: false, staleTime: 0, gcTime: 0, refetchOnMount: "always" });
}
/** Every reading of one course's content, in either mode: a confirmed session or placement change refreshes them. */
export function courseContentQueries(course: string) {
  return { predicate: (query: { queryKey: readonly unknown[] }) => query.queryKey[0] === "course-content" && query.queryKey[3] === course };
}
export function contentRead(query: ReturnType<typeof useCourseContent>): Read<CourseContent> {
  if (query.error instanceof ApiError && [401, 403, 404].includes(query.error.status)) return { status: "forbidden" };
  if (!query.data) return query.isError ? { status: "failed", sentence: "Course content could not be read.", retry: () => void query.refetch() } : { status: "loading" };
  if (query.isError) return { status: "stale", last: query.data, readAt: query.data.read_at, sentence: "Course content could not be refreshed.", refresh: () => void query.refetch() };
  return { status: "ready", value: query.data, readAt: query.data.read_at };
}
